import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography } from '@mui/material';
import { HotelSearchRoomInputWidgetItem } from './HotelSearchRoomInputWidgetItem';
import { RoomSelection } from './types';

export const HotelSearchRoomInputWidget = ({
  onRoomsChange,
  defaultRooms,
}: {
  onRoomsChange: (rooms: RoomSelection[]) => void;
  defaultRooms: RoomSelection[] | null;
}) => {
  const { t } = useTranslation();
  const nextId = useRef(defaultRooms?.length || 1);
  const addButton = useRef<HTMLButtonElement>(null);
  const [rooms, setRooms] = useState(() =>
    (defaultRooms?.length ? defaultRooms : [{ paxes: [30, 30] }]).map((room, id) => ({ ...room, id })),
  );
  const [newRoom, setNewRoom] = useState<number | null>(null);
  const update = (next: typeof rooms) => {
    setRooms(next);
    onRoomsChange(next.map(({ paxes }) => ({ paxes })));
  };
  return (
    <Stack gap={2} sx={{ pt: 1 }}>
      {rooms.map((room, index) => (
        <HotelSearchRoomInputWidgetItem
          key={room.id}
          room={room}
          roomIndex={index}
          autoFocus={index === 0 || room.id === newRoom}
          canRemove={rooms.length > 1}
          onRemove={() => {
            update(rooms.filter((item) => item.id !== room.id));
            addButton.current?.focus();
          }}
          onRoomChange={({ paxes }) => update(rooms.map((item) => (item.id === room.id ? { ...item, paxes } : item)))}
        />
      ))}
      <Button
        type="button"
        ref={addButton}
        variant="outlined"
        fullWidth
        disabled={rooms.length >= 30}
        onClick={() => {
          const id = nextId.current++;
          setNewRoom(id);
          update([...rooms, { id, paxes: [30, 30] }]);
        }}
      >
        {t('hotels.roomMenu.addRoom')}
      </Button>
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {t(rooms.length >= 30 ? 'hotels.roomMenu.roomLimit' : 'hotels.roomMenu.newRoomDefaults')}
      </Typography>
    </Stack>
  );
};
